/**
 * Calendar Prefetch Hook
 *
 * Extracts event loading and prefetching logic from calendar.tsx.
 * Handles background prefetching of adjacent months for instant navigation.
 */

import { useCallback } from 'react';
import type { CalendarScreenManager } from '@/lib/calendar/CalendarScreenManager';
import type { AgendaSection, FlatAgendaItem } from '@/types/calendar-ui';
import { buildEventWindow, buildMonthEventWindow, type EventWindow } from './use-calendar-screen-state';

const MAX_EVENT_CACHE_ENTRIES = 12;

const buildCacheKey = (calendarIds: string[], window: EventWindow): string => {
  const sorted = [...calendarIds].sort().join('|');
  return `${sorted}::${window.key}`;
};

type UseCalendarPrefetchProps = {
  manager: CalendarScreenManager;
  selectedCalendarIds: string[];
  buildEventWindow: typeof buildEventWindow;
  eventsCacheRef: React.MutableRefObject<Map<string, { sections: AgendaSection[]; flatItems: FlatAgendaItem[]; fetchedAt: number }>>;
  latestEventRequestRef: React.MutableRefObject<number>;
  selectedDateRef: React.MutableRefObject<string>;
  activeEventWindowRef: React.MutableRefObject<EventWindow>;
  isLoadingDataRef: React.MutableRefObject<boolean>;
  setSections: (sections: AgendaSection[]) => void;
  setFlatItems: (items: FlatAgendaItem[]) => void;
  setSelectedDate: (date: string) => void;
  setActiveEventWindow: (window: EventWindow) => void;
  setError: (error: string | null) => void;
  setIsLoading: (loading: boolean) => void;
  setIsSilentLoading: (loading: boolean) => void;
};

export function useCalendarPrefetch({
  manager,
  selectedCalendarIds,
  buildEventWindow,
  eventsCacheRef,
  latestEventRequestRef,
  selectedDateRef,
  activeEventWindowRef,
  isLoadingDataRef,
  setSections,
  setFlatItems,
  setSelectedDate,
  setActiveEventWindow,
  setError,
  setIsLoading,
  setIsSilentLoading,
}: UseCalendarPrefetchProps) {
  /**
   * Background prefetch adjacent months for instant navigation
   * Runs in background after main data loads, doesn't block UI
   */
  const prefetchAdjacentMonths = useCallback(
    async (currentDate: string) => {
      if (selectedCalendarIds.length === 0) {
        return;
      }

      const perfStart = __DEV__ ? performance.now() : 0;

      try {
        // Parse current date to get year/month
        const dateParts = currentDate.split('-').map(Number);
        const currentYear = dateParts[0];
        const currentMonth = dateParts[1] - 1; // 0-indexed

        // Calculate next and previous months
        const nextMonthDate = currentMonth === 11
          ? { year: currentYear + 1, month: 0 }
          : { year: currentYear, month: currentMonth + 1 };
        const prevMonthDate = currentMonth === 0
          ? { year: currentYear - 1, month: 11 }
          : { year: currentYear, month: currentMonth - 1 };

        // Build 6-week windows for adjacent months
        const nextWindow = buildMonthEventWindow(nextMonthDate.year, nextMonthDate.month);
        const prevWindow = buildMonthEventWindow(prevMonthDate.year, prevMonthDate.month);

        // Check which ones aren't cached
        const nextCacheKey = buildCacheKey(selectedCalendarIds, nextWindow);
        const prevCacheKey = buildCacheKey(selectedCalendarIds, prevWindow);

        const needsNextMonth = !eventsCacheRef.current.has(nextCacheKey);
        const needsPrevMonth = !eventsCacheRef.current.has(prevCacheKey);

        // Prefetch both in parallel if needed
        const prefetchPromises: Promise<void>[] = [];

        if (needsNextMonth) {
          prefetchPromises.push(
            (async () => {
              try {
                const { sections: nextSections, flatItems: nextFlatItems } = await manager.loadEvents(
                  selectedCalendarIds,
                  {
                    startDate: nextWindow.start,
                    endDate: nextWindow.end,
                    useMinimalFields: true, // Use minimal fields for faster prefetch
                  },
                );

                // Cache it for instant access later
                eventsCacheRef.current.set(nextCacheKey, {
                  sections: nextSections,
                  flatItems: nextFlatItems,
                  fetchedAt: Date.now(),
                });
              } catch (err) {
                // Silently fail - prefetch is optional
                console.debug('[Calendar] Failed to prefetch next month:', err);
              }
            })()
          );
        }

        if (needsPrevMonth) {
          prefetchPromises.push(
            (async () => {
              try {
                const { sections: prevSections, flatItems: prevFlatItems } = await manager.loadEvents(
                  selectedCalendarIds,
                  {
                    startDate: prevWindow.start,
                    endDate: prevWindow.end,
                    useMinimalFields: true, // Use minimal fields for faster prefetch
                  },
                );

                // Cache it for instant access later
                eventsCacheRef.current.set(prevCacheKey, {
                  sections: prevSections,
                  flatItems: prevFlatItems,
                  fetchedAt: Date.now(),
                });
              } catch (err) {
                // Silently fail - prefetch is optional
                console.debug('[Calendar] Failed to prefetch previous month:', err);
              }
            })()
          );
        }

        // Run prefetches without blocking
        await Promise.all(prefetchPromises);

        if (__DEV__ && prefetchPromises.length > 0) {
          const perfEnd = performance.now();
          console.log(`[Calendar Perf] Prefetch complete - ${(perfEnd - perfStart).toFixed(2)}ms (${prefetchPromises.length} months)`);
        }
      } catch (err) {
        // Silently fail - prefetch errors shouldn't affect user
        console.debug('[Calendar] Prefetch error:', err);
      }
    },
    [selectedCalendarIds, manager, buildEventWindow, eventsCacheRef],
  );

  /**
   * Load events from device calendar with caching and prefetching
   */
  const loadEventsFromDevice = useCallback(
    async ({
      forceRefresh = false,
      targetDate,
      silent = false,
    }: { forceRefresh?: boolean; targetDate?: string; silent?: boolean } = {}) => {
      // Set loading ref IMMEDIATELY to prevent race conditions
      isLoadingDataRef.current = true;

      if (selectedCalendarIds.length === 0) {
        setSections([]);
        setFlatItems([]);
        setIsLoading(false);
        isLoadingDataRef.current = false;
        return;
      }

      const dateForWindow = targetDate ?? selectedDateRef.current;
      // Use month-based window to load full 6 weeks of events for monthly calendar view
      const dateParts = dateForWindow.split('-').map(Number);
      const window = buildMonthEventWindow(dateParts[0], dateParts[1] - 1);
      setActiveEventWindow(window);
      activeEventWindowRef.current = window;

      const cacheKey = buildCacheKey(selectedCalendarIds, window);
      const cached = eventsCacheRef.current.get(cacheKey);

      const desiredDate = targetDate ?? selectedDateRef.current;

      const applySelectedDate = (sectionsData: AgendaSection[], flatItemsData: FlatAgendaItem[]) => {
        // Always maintain the desired date selection
        // The UI components handle scrolling to the nearest available event if the exact date has no events
        setSelectedDate(desiredDate);
      };

      if (!forceRefresh && cached) {
        setSections(cached.sections);
        setFlatItems(cached.flatItems);
        applySelectedDate(cached.sections, cached.flatItems);
        setError(null);
        setIsLoading(false);
        isLoadingDataRef.current = false;
        return;
      }

      const requestId = Date.now();
      latestEventRequestRef.current = requestId;

      const shouldShowBlockingLoader = !forceRefresh && !cached && !silent;
      const shouldResetLoading = shouldShowBlockingLoader || forceRefresh;

      if (shouldShowBlockingLoader) {
        setIsLoading(true);
      }

      // Show silent loading indicator when loading new window in background
      if (silent && !cached) {
        setIsSilentLoading(true);
      }

      try {
        const { sections: newSections, flatItems: newFlatItems } = await manager.loadEvents(
          selectedCalendarIds,
          {
            startDate: window.start,
            endDate: window.end,
          },
        );

        if (latestEventRequestRef.current !== requestId) {
          return;
        }

        setSections(newSections);
        setFlatItems(newFlatItems);
        applySelectedDate(newSections, newFlatItems);
        setError(null);

        eventsCacheRef.current.set(cacheKey, {
          sections: newSections,
          flatItems: newFlatItems,
          fetchedAt: Date.now(),
        });
        if (eventsCacheRef.current.size > MAX_EVENT_CACHE_ENTRIES) {
          const firstKey = eventsCacheRef.current.keys().next().value;
          if (firstKey) {
            eventsCacheRef.current.delete(firstKey);
          }
        }

        // Prefetch adjacent months in background (non-blocking)
        if (!silent) {
          setTimeout(() => {
            prefetchAdjacentMonths(dateForWindow);
          }, 100); // Small delay to ensure main UI updates first
        }
      } catch (err) {
        if (latestEventRequestRef.current !== requestId) {
          return;
        }
        setError((err as Error).message);
      } finally {
        if (latestEventRequestRef.current === requestId) {
          isLoadingDataRef.current = false;
          setIsSilentLoading(false);
          if (shouldResetLoading) {
            setIsLoading(false);
          }
        }
      }
    },
    [
      selectedCalendarIds,
      manager,
      buildEventWindow,
      eventsCacheRef,
      latestEventRequestRef,
      selectedDateRef,
      activeEventWindowRef,
      isLoadingDataRef,
      setSections,
      setFlatItems,
      setSelectedDate,
      setActiveEventWindow,
      setError,
      setIsLoading,
      setIsSilentLoading,
      prefetchAdjacentMonths,
    ],
  );

  return {
    prefetchAdjacentMonths,
    loadEventsFromDevice,
  };
}
